import { useState, useCallback, useEffect, useRef } from 'react';
import { Platform } from 'react-native';

interface PerformanceMetrics {
  avgResponseTime: number | null;
  minResponseTime: number | null;
  maxResponseTime: number | null;
  jsThreadBlockTime: number | null;
  longTasks: number;
  memoryUsed: number | null;
  memoryDelta: number | null;
  reduxDispatchCount: number;
  avgReduxDispatchTime: number | null;
  frameDrops: number;
  fps: number | null;
  renderTime: number | null;
  totalNetworkTime: number;
  totalReduxTime: number;
}

interface MetricsConfig {
  trackNetworkTiming: boolean;
  trackReduxPerformance: boolean;
  trackMemory: boolean;
  trackRendering: boolean;
  trackJSThread: boolean;
}

const usePerformanceMetrics = (config: MetricsConfig) => {
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    avgResponseTime: null,
    minResponseTime: null,
    maxResponseTime: null,
    jsThreadBlockTime: null,
    longTasks: 0,
    memoryUsed: null,
    memoryDelta: null,
    reduxDispatchCount: 0,
    avgReduxDispatchTime: null,
    frameDrops: 0,
    fps: null,
    renderTime: null,
    totalNetworkTime: 0,
    totalReduxTime: 0,
  });

  const observerRef = useRef<PerformanceObserver | null>(null);
  const frameCountRef = useRef(0);
  const lastFrameTimeRef = useRef(performance.now());
  const memoryBeforeRef = useRef<number | null>(null);
  const responseTimes = useRef<number[]>([]);
  const reduxDispatchTimes = useRef<number[]>([]);
  const longTasksRef = useRef<number[]>([]);

  // Track JavaScript thread blocking (long tasks)
  useEffect(() => {
    if (!config.trackJSThread) return;

    // Only available in web browsers, not React Native
    if (Platform.OS === 'web' && typeof PerformanceObserver !== 'undefined') {
      try {
        observerRef.current = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            // Long task is any task that blocks the main thread for 50ms or more
            if (entry.duration > 50) {
              longTasksRef.current.push(entry.duration);
              setMetrics(prev => ({
                ...prev,
                longTasks: prev.longTasks + 1,
                jsThreadBlockTime: (prev.jsThreadBlockTime || 0) + entry.duration,
              }));
            }
          }
        });

        observerRef.current.observe({ entryTypes: ['longtask'] });
      } catch (error) {
        console.log('PerformanceObserver not available:', error);
      }
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [config.trackJSThread]);

  // Track memory usage
  const trackMemory = useCallback(() => {
    if (!config.trackMemory) return;

    // Memory API is only available in Chrome and some browsers
    if (Platform.OS === 'web' && (performance as any).memory) {
      const memory = (performance as any).memory;
      const currentMemory = memory.usedJSHeapSize;

      if (!memoryBeforeRef.current) {
        memoryBeforeRef.current = currentMemory;
      }

      const memoryDelta = currentMemory - memoryBeforeRef.current;

      setMetrics(prev => ({
        ...prev,
        memoryUsed: currentMemory,
        memoryDelta: memoryDelta,
      }));
    }
  }, [config.trackMemory]);

  // Track FPS and frame drops
  const trackFrameRate = useCallback(() => {
    if (!config.trackRendering) return;

    const currentTime = performance.now();
    const deltaTime = currentTime - lastFrameTimeRef.current;

    if (deltaTime > 0) {
      const currentFps = 1000 / deltaTime;

      // Consider it a frame drop if FPS falls below 30
      if (currentFps < 30 && frameCountRef.current > 0) {
        setMetrics(prev => ({
          ...prev,
          frameDrops: prev.frameDrops + 1,
        }));
      }

      setMetrics(prev => ({
        ...prev,
        fps: currentFps,
      }));
    }

    lastFrameTimeRef.current = currentTime;
    frameCountRef.current++;

    if (config.trackRendering) {
      requestAnimationFrame(trackFrameRate);
    }
  }, [config.trackRendering]);

  // Start FPS tracking
  useEffect(() => {
    if (config.trackRendering) {
      requestAnimationFrame(trackFrameRate);
    }
  }, [config.trackRendering, trackFrameRate]);

  // Record network response time
  const recordResponseTime = useCallback((duration: number) => {
    if (!config.trackNetworkTiming) return;

    responseTimes.current.push(duration);

    const times = responseTimes.current;
    const avg = times.reduce((a, b) => a + b, 0) / times.length;
    const min = Math.min(...times);
    const max = Math.max(...times);
    const total = times.reduce((a, b) => a + b, 0);

    setMetrics(prev => ({
      ...prev,
      avgResponseTime: avg,
      minResponseTime: min,
      maxResponseTime: max,
      totalNetworkTime: total,
    }));

    // Also track memory after each request
    trackMemory();
  }, [config.trackNetworkTiming, trackMemory]);

  // Record Redux dispatch time
  const recordReduxDispatch = useCallback((duration: number) => {
    if (!config.trackReduxPerformance) return;

    reduxDispatchTimes.current.push(duration);

    const times = reduxDispatchTimes.current;
    const avg = times.reduce((a, b) => a + b, 0) / times.length;
    const total = times.reduce((a, b) => a + b, 0);

    setMetrics(prev => ({
      ...prev,
      reduxDispatchCount: prev.reduxDispatchCount + 1,
      avgReduxDispatchTime: avg,
      totalReduxTime: total,
    }));
  }, [config.trackReduxPerformance]);

  // Calculate render time
  const recordRenderTime = useCallback((startTime: number) => {
    if (!config.trackRendering) return;

    const endTime = performance.now();
    const renderTime = endTime - startTime;

    setMetrics(prev => ({
      ...prev,
      renderTime: renderTime,
    }));
  }, [config.trackRendering]);

  // Reset all metrics
  const resetMetrics = useCallback(() => {
    setMetrics({
      avgResponseTime: null,
      minResponseTime: null,
      maxResponseTime: null,
      jsThreadBlockTime: null,
      longTasks: 0,
      memoryUsed: null,
      memoryDelta: null,
      reduxDispatchCount: 0,
      avgReduxDispatchTime: null,
      frameDrops: 0,
      fps: null,
      renderTime: null,
      totalNetworkTime: 0,
      totalReduxTime: 0,
    });

    responseTimes.current = [];
    reduxDispatchTimes.current = [];
    longTasksRef.current = [];
    frameCountRef.current = 0;
    memoryBeforeRef.current = null;
    lastFrameTimeRef.current = performance.now();
  }, []);

  // Get performance summary
  const getPerformanceSummary = useCallback(() => {
    const summary: any = {
      network: {
        avgResponseTime: metrics.avgResponseTime,
        minResponseTime: metrics.minResponseTime,
        maxResponseTime: metrics.maxResponseTime,
        totalTime: metrics.totalNetworkTime,
        requestCount: responseTimes.current.length,
      },
      redux: {
        dispatchCount: metrics.reduxDispatchCount,
        avgDispatchTime: metrics.avgReduxDispatchTime,
        totalTime: metrics.totalReduxTime,
      },
      jsThread: {
        blockingTime: metrics.jsThreadBlockTime,
        longTasks: metrics.longTasks,
        avgLongTaskDuration: longTasksRef.current.length > 0
          ? longTasksRef.current.reduce((a, b) => a + b, 0) / longTasksRef.current.length
          : 0,
      },
      memory: {
        used: metrics.memoryUsed,
        delta: metrics.memoryDelta,
      },
      rendering: {
        fps: metrics.fps,
        frameDrops: metrics.frameDrops,
        renderTime: metrics.renderTime,
      },
    };

    // Add performance score (0-100)
    let score = 100;

    // Deduct points for poor network performance
    if (metrics.avgResponseTime) {
      if (metrics.avgResponseTime > 3000) score -= 20;
      else if (metrics.avgResponseTime > 1500) score -= 10;
      else if (metrics.avgResponseTime > 500) score -= 5;
    }

    // Deduct points for JS thread blocking
    if (metrics.jsThreadBlockTime) {
      if (metrics.jsThreadBlockTime > 5000) score -= 20;
      else if (metrics.jsThreadBlockTime > 2000) score -= 10;
      else if (metrics.jsThreadBlockTime > 500) score -= 5;
    }

    // Deduct points for frame drops
    if (metrics.frameDrops > 10) score -= 15;
    else if (metrics.frameDrops > 5) score -= 10;
    else if (metrics.frameDrops > 2) score -= 5;

    // Deduct points for memory usage
    if (metrics.memoryDelta) {
      const deltaMB = metrics.memoryDelta / 1024 / 1024;
      if (deltaMB > 50) score -= 15;
      else if (deltaMB > 20) score -= 10;
      else if (deltaMB > 10) score -= 5;
    }

    summary.performanceScore = Math.max(0, score);
    summary.grade =
      score >= 90 ? 'A' :
      score >= 80 ? 'B' :
      score >= 70 ? 'C' :
      score >= 60 ? 'D' : 'F';

    return summary;
  }, [metrics]);

  // Middleware for Redux performance tracking
  const reduxMiddleware = useCallback((store: any) => (next: any) => (action: any) => {
    if (!config.trackReduxPerformance) return next(action);

    const startTime = performance.now();
    const result = next(action);
    const endTime = performance.now();
    const duration = endTime - startTime;

    recordReduxDispatch(duration);

    return result;
  }, [config.trackReduxPerformance, recordReduxDispatch]);

  return {
    metrics,
    resetMetrics,
    recordResponseTime,
    recordReduxDispatch,
    recordRenderTime,
    getPerformanceSummary,
    reduxMiddleware,
  };
};

export default usePerformanceMetrics;